// @ts-nocheck
/**
 * Country Code Directions list — redesigned to obd3-gui pattern
 * (FillPage + PageHeader + DataTable + ConfirmDialog). Preserved:
 * Storage.getItem('user') gate for superadmin actions, alpha sort by
 * country name, search strips leading "+", status pill (Enabled / Disabled).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Globe2, Pencil, Plus, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import Loader from '@/components/partials/components/Loader';
import {
  API_COUNTRY_CODE_DIRECTIONS_MASTER_LIST,
  API_COUNTRY_CODE_DIRECTIONS_MASTER_DELETE,
} from '@/service/url';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import { Storage } from '@/utilities/setting';

const CountryCodeDirectionsList = () => {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    try {
      const user = JSON.parse(Storage.getItem('user'));
      setIsSuperAdmin(user?.role === USER_ROLES.SUPERADMIN);
    } catch (_) {
      /* ignore */
    }
    loadList();
  }, []);

  const loadList = () => {
    setLoading(true);
    getApiCall(API_COUNTRY_CODE_DIRECTIONS_MASTER_LIST, {})
      .then((data: any) => setRows(Array.isArray(data) ? data : []))
      .catch((err: any) =>
        toast.error(friendlyApiError(err) || 'Failed to load directions'),
      )
      .finally(() => setLoading(false));
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    setDeleting(true);
    postApiCall(API_COUNTRY_CODE_DIRECTIONS_MASTER_DELETE(pendingDelete.id), {})
      .then(() => {
        toast.success('Deleted');
        setPendingDelete(null);
        loadList();
      })
      .catch((err: any) =>
        toast.error(friendlyApiError(err) || 'Failed to delete direction'),
      )
      .finally(() => setDeleting(false));
  };

  const filteredRows = useMemo(() => {
    return [...rows]
      .sort((a: any, b: any) =>
        (a?.country_code?.name || '').localeCompare(b?.country_code?.name || ''),
      )
      .filter((r: any) => {
        const country = r?.country_code?.name?.toLowerCase() || '';
        const callingCode = String(r?.country_code?.calling_code || '');
        const q = search.toLowerCase().trim().replace('+', '');
        return country.includes(q) || callingCode.includes(q);
      });
  }, [rows, search]);

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        id: 'index',
        header: '#',
        size: 60,
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{row.index + 1}</span>
        ),
      },
      {
        accessorFn: (row: any) => row?.country_code?.name,
        id: 'country',
        header: 'Country',
        size: 260,
        cell: ({ row }) => {
          const nm = row.original.country_code?.name || '—';
          const initials = (nm || '')
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((w: string) => w[0])
            .join('')
            .toUpperCase() || '—';
          return (
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
                {initials}
              </span>
              <span className="font-medium text-foreground">{nm}</span>
            </div>
          );
        },
      },
      {
        accessorFn: (row: any) => row?.country_code?.calling_code,
        id: 'calling_code',
        header: 'Calling Code',
        size: 140,
        cell: ({ row }) =>
          row.original.country_code?.calling_code ? (
            <span className="inline-flex items-center rounded-md bg-muted/60 px-2 py-0.5 font-mono text-xs font-semibold tabular-nums text-foreground ring-1 ring-inset ring-border/60">
              +{row.original.country_code.calling_code}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'is_enabled',
        header: 'Status',
        size: 130,
        cell: ({ row }) => {
          const on = !!row.original.is_enabled;
          return (
            <span
              className={
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
                (on
                  ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                  : 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300')
              }
            >
              {on ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
              {on ? 'Enabled' : 'Disabled'}
            </span>
          );
        },
      },
    ];

    if (isSuperAdmin) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        size: 110,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const nm = row.original.country_code?.name || `#${row.original.id}`;
          return (
            <div className="flex items-center gap-1">
              <button
                type="button"
                title={`Edit ${nm}`}
                onClick={() =>
                  navigate(
                    `/${ROUTES.COUNTRY_CODE_DIRECTIONS.EDIT.replace(':id', row.original.id)}`,
                  )
                }
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors hover:bg-primary/15"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                title={`Delete ${nm}`}
                onClick={() => setPendingDelete(row.original)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 text-destructive transition-colors hover:bg-destructive/15"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        },
      });
    }

    return cols;
  }, [isSuperAdmin, navigate]);

  const stats = useMemo(() => {
    const total = rows.length;
    const enabled = rows.filter((r: any) => !!r.is_enabled).length;
    return { total, enabled, disabled: total - enabled };
  }, [rows]);

  // Show a full-page loader on the first fetch so the reader gets clear
  // "Loading" feedback (matches Users, Companies etc). Once rows are in,
  // subsequent refetches keep the current data on screen — the DataTable's
  // own skeleton bar handles the in-place refresh state.
  if (loading && rows.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Country Codes"
        description="Master list of country calling codes and their direction (outbound enabled)."
        actions={
          isSuperAdmin && (
            <Button
              onClick={() => navigate(`/${ROUTES.COUNTRY_CODE_DIRECTIONS.CREATE}`)}
            >
              <Plus className="h-4 w-4" />
              Add Country Code
            </Button>
          )
        }
      />

      {/* Stat strip — Total / Enabled / Disabled */}
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs shadow-neu-raised">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Globe2 className="h-3.5 w-3.5" />
          </span>
          <span className="text-muted-foreground">Total</span>
          <span className="font-semibold text-foreground tabular-nums">{stats.total}</span>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs shadow-neu-raised dark:border-emerald-900/60 dark:bg-emerald-950/30">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300">
            <CheckCircle2 className="h-3.5 w-3.5" />
          </span>
          <span className="text-emerald-700 dark:text-emerald-300">Enabled</span>
          <span className="font-semibold text-emerald-800 tabular-nums dark:text-emerald-200">
            {stats.enabled}
          </span>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs shadow-neu-raised dark:border-slate-700 dark:bg-slate-800/40">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
            <XCircle className="h-3.5 w-3.5" />
          </span>
          <span className="text-slate-700 dark:text-slate-300">Disabled</span>
          <span className="font-semibold text-slate-800 tabular-nums dark:text-slate-200">
            {stats.disabled}
          </span>
        </div>
      </div>

      <DataTable
        tableId="country-code-directions:list:v1"
        columns={columns}
        data={filteredRows}
        isLoading={loading}
        searchPlaceholder="Search by country or calling code…"
        emptyTitle="No country codes"
        emptyDescription="No records match the current filters."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={isSuperAdmin ? { right: ['actions'] } : undefined}
        pageSize={20}
        disableExport
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this country code?"
        description={
          <>
            Country code{' '}
            <span className="font-medium text-foreground">
              {pendingDelete?.country_code?.name || `#${pendingDelete?.id}`}
            </span>{' '}
            will be permanently deleted from the master list.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
};

export default CountryCodeDirectionsList;
